import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import { ContractOfDay, DailyWorkJoin, DailyWorkDayJoin } from "@/repositories/daily-work.repository";
import { createDailyWorkDay, updateDailyWorkDay, listDailyWorkForVendor } from "../daily-work.api";
import { ServicesRepository } from "@/repositories/services.repository";
import { IndividualsRepository } from "@/repositories/individuals.repository";
import { DailyWorkRateProfileRepository } from "@/repositories/daily-work-rate-profile.repository";
import type { Service } from "@shyne/shared/api";
import { daily_work_status, daily_work_day_status } from "@/models/daily-work.model";
import { Badge } from "@/components/ui/badge";
import { X, CalendarIcon, ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from "date-fns";
import moment from "moment";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

interface DayConfigurationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dailyWork?: ContractOfDay | null;
  workDate: string; // YYYY-MM-DD format
  existingDay: DailyWorkDayJoin | null;
  copyDay?: DailyWorkDayJoin | null; // Day to copy from
  onSuccess: () => void;
}

type RateProfileSource = "profile" | "manual" | "none";

type AssignableIndividual = {
  id: string;
  name: string;
  organizationIndividualId: string;
};

type AssignableSubs = {
  id: string;
  name: string;
  organizationSubsId: string;
};

export default function DayConfigurationModal({
  open,
  onOpenChange,
  dailyWork: dailyWorkProp,
  workDate,
  existingDay,
  copyDay,
  onSuccess,
}: DayConfigurationModalProps) {
  const isCopyMode = !!copyDay;
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [individuals, setIndividuals] = useState<AssignableIndividual[]>([]);
  const [subs, setSubs] = useState<AssignableSubs[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [serviceComboboxOpen, setServiceComboboxOpen] = useState(false);
  const [individualComboboxOpen, setIndividualComboboxOpen] = useState(false);
  const [subsComboboxOpen, setSubsComboboxOpen] = useState(false);

  // Daily work selector (when dailyWorkProp is not provided)
  const [dailyWorkOptions, setDailyWorkOptions] = useState<DailyWorkJoin[]>([]);
  const [selectedDailyWork, setSelectedDailyWork] = useState<ContractOfDay | null>(dailyWorkProp ?? null);
  const [dailyWorkComboboxOpen, setDailyWorkComboboxOpen] = useState(false);
  const dailyWork = dailyWorkProp ?? selectedDailyWork;
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    isCopyMode ? undefined : !dailyWorkProp ? parseISO(workDate) : parseISO(workDate)
  );
  const [service, setService] = useState(
    isCopyMode ? (copyDay?.service.id ?? "") : (existingDay?.service.id ?? "")
  );
  const [tasks, setTasks] = useState<string[]>(
    isCopyMode ? (copyDay?.tasks ?? []) : (existingDay?.tasks ?? [])
  );
  const [newTask, setNewTask] = useState("");
  const [assignmentType, setAssignmentType] = useState<"individual" | "company" | null>(
    isCopyMode
      ? (copyDay?.assigned_org ? "company" : copyDay?.assigned_user ? "individual" : null)
      : (existingDay?.assigned_org ? "company" : existingDay?.assigned_user ? "individual" : null)
  );
  const [assignedUser, setAssignedUser] = useState<string | null>(
    isCopyMode ? (copyDay?.assigned_user?.id ?? null) : (existingDay?.assigned_user?.id ?? null)
  );
  const [assignedOrg, setAssignedOrg] = useState<string | null>(
    isCopyMode ? (copyDay?.assigned_org?.id ?? null) : (existingDay?.assigned_org?.id ?? null)
  );
  const [hourlyRate, setHourlyRate] = useState<string>(
    isCopyMode
      ? (copyDay?.assigned_org
          ? (copyDay.subs_payment?.toString() ?? "")
          : copyDay?.assigned_user
            ? (copyDay.individual_payment?.toString() ?? "")
          : "")
      : (existingDay?.assigned_org
          ? (existingDay.subs_payment?.toString() ?? "")
          : existingDay?.assigned_user
            ? (existingDay.individual_payment?.toString() ?? "")
          : "")
  );
  const [rateSource, setRateSource] = useState<RateProfileSource>(
    isCopyMode
      ? "manual"
      : existingDay?.assigned_org
        ? (existingDay.subs_payment_source ?? "none")
        : existingDay?.assigned_user
          ? (existingDay.individual_payment_source ?? "none")
          : "none"
  );
  const [maxHours, setMaxHours] = useState<string>(
    isCopyMode ? (copyDay?.max_hours != null ? String(copyDay.max_hours) : "") : (existingDay?.max_hours != null ? String(existingDay.max_hours) : "")
  );

  const formatRate = (rate: number | null | undefined) => (rate == null ? "" : rate.toFixed(2));

  const applyProfileRate = useCallback(async (type: "individual" | "company", resourceId: string | null, serviceId = service) => {
    if (!resourceId || !dailyWork?.vendor.id || !serviceId) {
      setHourlyRate("");
      setRateSource("none");
      return;
    }

    try {
      const { rate, source } = await DailyWorkRateProfileRepository.profileRate({
        payer: dailyWork.vendor.id,
        payee: type === "individual" ? { worker: resourceId } : { subcontractor: resourceId },
        service: serviceId,
      });

      setHourlyRate(rate == null ? "" : formatRate(rate));
      setRateSource(source);
    } catch (error) {
      console.error("Failed to apply Daily Work profile rate:", error);
      setHourlyRate("");
      setRateSource("none");
    }
  }, [dailyWork?.vendor.id, service]);

  // Load daily work options when no dailyWorkProp is provided
  useEffect(() => {
    if (!open || dailyWorkProp) return;

    listDailyWorkForVendor(1, 500, { status: daily_work_status.active }, "created_at", "desc")
      .then((result) => {
        setDailyWorkOptions(result.data);
      })
      .catch((error) => {
        console.error("Failed to fetch daily work records:", error);
        toast.error("Failed to load properties");
      });
  }, [open, dailyWorkProp]);

  // Reset selected daily work when modal opens/closes
  useEffect(() => {
    if (!open) {
      if (!dailyWorkProp) {
        setSelectedDailyWork(null);
      }
    }
  }, [open, dailyWorkProp]);

  useEffect(() => {
    if (!open) return;

    ServicesRepository.getParentServices()
      .then((result) => setServices(result.data))
      .catch((error) => {
        console.error("Failed to fetch services:", error);
        toast.error("Failed to load services");
      });

    // Use listForAssignment which fetches all active individuals without pagination limits
    IndividualsRepository.listForAssignment()
      .then((individuals) => {
        setIndividuals(
          individuals.map((emp) => ({
            id: emp.id,
            name: emp.name,
            organizationIndividualId: emp.organizationIndividualId,
          }))
        );
      })
      .catch((error) => {
        console.error("Failed to fetch individuals:", error);
        toast.error("Failed to load individuals");
      });

    // Use listSubsForAssignment which fetches all subs without pagination limits
    IndividualsRepository.listSubsForAssignment()
      .then((subs) => {
        setSubs(
          subs.map((sub) => ({
            id: sub.id,
            name: sub.name,
            organizationSubsId: sub.organizationSubsId,
          }))
        );
      })
      .catch((error) => {
        console.error("Failed to fetch subs:", error);
        toast.error("Failed to load companies");
      });
  }, [open]);

  useEffect(() => {
    if (!open || !isCopyMode) return;

    if (assignmentType === "individual" && assignedUser) {
      void applyProfileRate("individual", assignedUser);
    } else if (assignmentType === "company" && assignedOrg) {
      void applyProfileRate("company", assignedOrg);
    }
  }, [open, isCopyMode, assignmentType, assignedUser, assignedOrg, applyProfileRate]);

  useEffect(() => {
    if (!open) return;

    setNewTask("");

    if (isCopyMode && copyDay) {
      setService(copyDay.service.id);
      setTasks(copyDay.tasks);
      setAssignmentType(copyDay.assigned_org ? "company" : copyDay.assigned_user ? "individual" : null);
      setAssignedUser(copyDay.assigned_user?.id ?? null);
      setAssignedOrg(copyDay.assigned_org?.id ?? null);
      setHourlyRate(
        copyDay.assigned_org
            ? (copyDay.subs_payment?.toString() ?? "")
            : copyDay.assigned_user
              ? (copyDay.individual_payment?.toString() ?? "")
            : ""
      );
      setRateSource("manual");
      setMaxHours(copyDay.max_hours != null ? String(copyDay.max_hours) : "");
      setSelectedDate(undefined); // Reset date for copy mode
    } else if (existingDay) {
      setService(existingDay.service.id);
      setTasks(existingDay.tasks);
      setAssignmentType(existingDay.assigned_org ? "company" : existingDay.assigned_user ? "individual" : null);
      setAssignedUser(existingDay.assigned_user?.id ?? null);
      setAssignedOrg(existingDay.assigned_org?.id ?? null);
      setHourlyRate(
        existingDay.assigned_org
            ? (existingDay.subs_payment?.toString() ?? "")
            : existingDay.assigned_user
              ? (existingDay.individual_payment?.toString() ?? "")
            : ""
      );
      setRateSource((existingDay.assigned_org ? existingDay.subs_payment_source : existingDay.individual_payment_source) ?? "manual");
      setMaxHours(existingDay.max_hours != null ? String(existingDay.max_hours) : "");
      setSelectedDate(parseISO(workDate));
    } else {
      setService("");
      setTasks([]);
      setNewTask("");
      setAssignmentType(null);
      setAssignedUser(null);
      setAssignedOrg(null);
      setHourlyRate("");
      setRateSource("none");
      setMaxHours("");
      setSelectedDate(parseISO(workDate));
    }
  }, [open, existingDay, copyDay, isCopyMode, workDate]);

  const handleAddTask = () => {
    if (newTask.trim()) {
      setTasks([...tasks, newTask.trim()]);
      setNewTask("");
    }
  };

  const handleRemoveTask = (index: number) => {
    setTasks(tasks.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dailyWork) {
      toast.error("Please select a property");
      return;
    }
    if (!service) {
      toast.error("Please select a service");
      return;
    }
    if (tasks.length === 0) {
      toast.error("Please add at least one task");
      return;
    }
    if ((isCopyMode || !dailyWorkProp) && !selectedDate) {
      toast.error("Please select a date");
      return;
    }

    setLoading(true);
    try {
      const targetWorkDate = (isCopyMode || !dailyWorkProp) && selectedDate ? format(selectedDate, "yyyy-MM-dd") : workDate;
      const assignment =
        assignmentType === "individual" && assignedUser
          ? { worker: assignedUser }
          : assignmentType === "company" && assignedOrg
            ? { subcontractor: assignedOrg }
            : null;
      const typedRate = hourlyRate.trim() === "" ? NaN : parseFloat(hourlyRate);
      const rate = assignment && Number.isFinite(typedRate) ? Math.round(typedRate * 100) / 100 : undefined;
      const maxHoursNum = maxHours.trim() ? parseFloat(maxHours) : NaN;
      const dayMaxHours = Number.isFinite(maxHoursNum) ? maxHoursNum : undefined;

      if (existingDay && !isCopyMode) {
        await updateDailyWorkDay(existingDay.id, { service, tasks, assignment, rate, maxHours: dayMaxHours });
        toast.success("Day configuration updated");
      } else {
        await createDailyWorkDay(dailyWork.id, { date: targetWorkDate, service, tasks, assignment, rate, maxHours: dayMaxHours });
        toast.success(isCopyMode ? "Day configuration copied" : "Day configuration created");
      }
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || "Failed to save day configuration");
    } finally {
      setLoading(false);
    }
  };

  // Get date range for calendar (start_date to end_date of daily work)
  const getDateRange = () => {
    if (!dailyWork?.start_date) return null;
    const start = parseISO(dailyWork.start_date);
    const end = dailyWork.end_date ? parseISO(dailyWork.end_date) : moment().toDate();
    return { start, end };
  };

  const dateRange = getDateRange();
  const isDateDisabled = (date: Date) => {
    if (!dateRange) return true;
    return !isWithinInterval(date, { start: startOfDay(dateRange.start), end: endOfDay(dateRange.end) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>
              {isCopyMode
                ? "Copy Day Configuration"
                : !dailyWorkProp
                  ? "Add Daily Work Day"
                  : `Configure ${format(moment(workDate).toDate(), "EEEE, MMMM d, yyyy")}`}
            </span>
            {existingDay?.status && (() => {
              const statusColors: Record<daily_work_day_status, string> = {
                [daily_work_day_status.pending]: "bg-gray-100 text-gray-700",
                [daily_work_day_status.assigned]: "bg-blue-100 text-blue-700",
                [daily_work_day_status.accepted]: "bg-yellow-100 text-yellow-700",
                [daily_work_day_status.inprogress]: "bg-orange-100 text-orange-700",
                [daily_work_day_status.completed]: "bg-green-100 text-green-700",
              };
              return <Badge variant="secondary" className={statusColors[existingDay.status]}>{existingDay.status}</Badge>;
            })()}
          </DialogTitle>
          <DialogDescription>
            {isCopyMode
              ? "Copy configuration to a new date"
              : !dailyWorkProp
                ? "Select a property and configure the day"
                : "Set up tasks, assignments, and payments for this date"}
          </DialogDescription>
        </DialogHeader>
        {existingDay?.recurring && !isCopyMode && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {existingDay.schedule_detached_at
              ? "This day was generated by the Master Schedule and has been edited by hand, so the schedule no longer updates or removes it."
              : "This day is generated by the Master Schedule. Saving changes here detaches this date from the schedule: your edits stick, and later row changes or the nightly sync will not touch it. Delete the day instead if this date should be skipped."}
          </div>
        )}
        <div ref={containerRef}>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Property selector (when no dailyWork is provided) */}
              {!dailyWorkProp && (
                <div className="space-y-2">
                  <Label>Property *</Label>
                  <Popover open={dailyWorkComboboxOpen} onOpenChange={setDailyWorkComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={dailyWorkComboboxOpen}
                        className="w-full justify-between font-normal"
                        data-testid="daily-work-property-trigger"
                      >
                        {selectedDailyWork
                          ? selectedDailyWork.property.name
                          : "Select a property"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start" container={containerRef.current}>
                      <Command>
                        <CommandInput placeholder="Search property..." />
                        <CommandList>
                          <CommandEmpty>No properties found.</CommandEmpty>
                          <CommandGroup>
                            {dailyWorkOptions.map((dw) => (
                              <CommandItem
                                key={dw.id}
                                value={dw.property.name}
                                data-testid={`daily-work-property-option-${dw.id}`}
                                onSelect={() => {
                                  setSelectedDailyWork(dw);
                                  setDailyWorkComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedDailyWork?.id === dw.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div>
                                  <div>{dw.property.name}</div>
                                  {dw.property.address && (
                                    <div className="text-xs text-muted-foreground">{dw.property.address}</div>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Date picker for add-from-calendar and copy modes */}
              {!dailyWorkProp && !isCopyMode && (
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" container={containerRef.current}>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          setSelectedDate(date);
                        }}
                        disabled={selectedDailyWork ? isDateDisabled : undefined}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {dateRange && selectedDailyWork && (
                    <p className="text-xs text-muted-foreground">
                      Available dates: {format(dateRange.start, "MMM d, yyyy")} - {format(dateRange.end, "MMM d, yyyy")}
                    </p>
                  )}
                </div>
              )}

              {isCopyMode && (
                <div className="space-y-2">
                  <Label>Select Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" container={containerRef.current}>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => {
                          setSelectedDate(date);
                        }}
                        disabled={isDateDisabled}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {dateRange && (
                    <p className="text-xs text-muted-foreground">
                      Available dates: {format(dateRange.start, "MMM d, yyyy")} - {format(dateRange.end, "MMM d, yyyy")}
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="service">Service *</Label>
                <Popover open={serviceComboboxOpen} onOpenChange={setServiceComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={serviceComboboxOpen}
                      className="w-full justify-between font-normal"
                      data-testid="daily-work-service-trigger"
                    >
                      {service
                        ? services.find((svc) => svc.id === service)?.name || "Select a service"
                        : "Select a service"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start" container={containerRef.current}>
                    <Command>
                      <CommandInput placeholder="Search service..." />
                      <CommandList>
                        <CommandEmpty>No services found.</CommandEmpty>
                        <CommandGroup>
                          {services.map((svc) => (
                            <CommandItem
                              key={svc.id}
                              value={svc.name}
                              data-testid={`daily-work-service-option-${svc.id}`}
                              onSelect={() => {
                                setService(svc.id);
                                if (assignmentType === "individual" && assignedUser) {
                                  void applyProfileRate("individual", assignedUser, svc.id);
                                } else if (assignmentType === "company" && assignedOrg) {
                                  void applyProfileRate("company", assignedOrg, svc.id);
                                }
                                setServiceComboboxOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  service === svc.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {svc.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {dailyWorkProp && (
                  <p className="text-xs text-muted-foreground">
                    This configuration is for a specific date: {format(moment(workDate).toDate(), "MMMM d, yyyy")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Special Tasks *</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    placeholder="Enter a special task"
                    data-testid="daily-work-task-input"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddTask();
                      }
                    }}
                  />
                  <Button
                    type="button"
                    onClick={handleAddTask}
                    disabled={!newTask.trim()}
                    data-testid="daily-work-task-add-button"
                  >
                    Add
                  </Button>
                </div>
                {tasks.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {tasks.map((task, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded">
                        <span className="flex-1">{task}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveTask(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Assignment Type</Label>
                <Select
                  value={assignmentType || ""}
                  onValueChange={(value) => {
                    if (value === "__clear__") {
                      setAssignmentType(null);
                      setAssignedUser(null);
                      setAssignedOrg(null);
                      setHourlyRate("");
                      setRateSource("none");
                    } else {
                      const newType = value === "individual" ? "individual" : value === "company" ? "company" : null;
                      setAssignmentType(newType);
                      if (newType !== "individual") setAssignedUser(null);
                      if (newType !== "company") setAssignedOrg(null);
                      setHourlyRate("");
                      setRateSource("none");
                    }
                  }}
                >
                  <SelectTrigger data-testid="daily-work-assignment-type-trigger">
                    <SelectValue placeholder="Select assignment type (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    {assignmentType && <SelectItem value="__clear__">Clear selection</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              {assignmentType === "individual" && (
                <div className="space-y-2">
                  <Label htmlFor="assignedUser">Individual</Label>
                  <Popover open={individualComboboxOpen} onOpenChange={setIndividualComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={individualComboboxOpen}
                        className="w-full justify-between font-normal"
                        data-testid="daily-work-individual-trigger"
                      >
                        {assignedUser
                          ? individuals.find((emp) => emp.id === assignedUser)?.name || "Select individual"
                          : "Select individual (optional)"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start" container={containerRef.current}>
                      <Command>
                        <CommandInput placeholder="Search individual..." />
                        <CommandList>
                          <CommandEmpty>No individuals found.</CommandEmpty>
                          <CommandGroup>
                            {assignedUser && (
                              <CommandItem
                                value="__clear__"
                                onSelect={() => {
                                  setAssignedUser(null);
                                  void applyProfileRate("individual", null);
                                  setIndividualComboboxOpen(false);
                                }}
                              >
                                <Check className="mr-2 h-4 w-4 opacity-0" />
                                Clear selection
                              </CommandItem>
                            )}
                            {individuals.map((emp) => (
                              <CommandItem
                                key={emp.id}
                                value={emp.name}
                                data-testid={`daily-work-individual-option-${emp.organizationIndividualId}`}
                                onSelect={() => {
                                  const nextUser = emp.id === assignedUser ? null : emp.id;
                                  setAssignedUser(nextUser);
                                  void applyProfileRate("individual", nextUser);
                                  setIndividualComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    assignedUser === emp.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {emp.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {assignmentType === "company" && (
                <div className="space-y-2">
                  <Label htmlFor="assignedOrg">Company</Label>
                  <Popover open={subsComboboxOpen} onOpenChange={setSubsComboboxOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={subsComboboxOpen}
                        className="w-full justify-between font-normal"
                        data-testid="daily-work-company-trigger"
                      >
                        {assignedOrg
                          ? subs.find((sub) => sub.id === assignedOrg)?.name || "Select company"
                          : "Select company (optional)"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start" container={containerRef.current}>
                      <Command>
                        <CommandInput placeholder="Search company..." />
                        <CommandList>
                          <CommandEmpty>No companies found.</CommandEmpty>
                          <CommandGroup>
                            {assignedOrg && (
                              <CommandItem
                                value="__clear__"
                                onSelect={() => {
                                  setAssignedOrg(null);
                                  void applyProfileRate("company", null);
                                  setSubsComboboxOpen(false);
                                }}
                              >
                                <Check className="mr-2 h-4 w-4 opacity-0" />
                                Clear selection
                              </CommandItem>
                            )}
                            {subs.map((sub) => (
                              <CommandItem
                                key={sub.id}
                                value={sub.name}
                                data-testid={`daily-work-company-option-${sub.organizationSubsId}`}
                                onSelect={() => {
                                  const nextOrg = sub.id === assignedOrg ? null : sub.id;
                                  setAssignedOrg(nextOrg);
                                  void applyProfileRate("company", nextOrg);
                                  setSubsComboboxOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    assignedOrg === sub.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {sub.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {assignmentType && (
                <div className="space-y-2">
                  <Label htmlFor="hourlyRate">Hourly rate</Label>
                  <Input
                    id="hourlyRate"
                    data-testid="daily-work-hourly-rate-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={rateSource === "profile" ? "Profile rate" : "Manual override"}
                    value={hourlyRate}
                    onChange={(e) => {
                      setHourlyRate(e.target.value);
                      setRateSource(e.target.value ? "manual" : "none");
                    }}
                  />
                  <p className="text-xs text-muted-foreground" data-testid="daily-work-hourly-rate-source">
                    {rateSource === "profile" ? "Profile rate applied" : rateSource === "manual" ? "Manual override" : "No profile rate"}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="maxHours">Max hours (optional)</Label>
                <Input
                  id="maxHours"
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="e.g. 8"
                  value={maxHours}
                  onChange={(e) => setMaxHours(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !dailyWork || !service || tasks.length === 0 || ((isCopyMode || !dailyWorkProp) && !selectedDate)}
                data-testid="daily-work-day-save-button"
              >
                {loading ? "Saving..." : isCopyMode ? "Copy" : existingDay ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
